import { useState } from "react";
import { Copy, Heart, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

export default function Categories({ categories, onFavorite }) {
  const [openId, setOpenId] = useState(null);

  if (!categories?.length) return null;

  const copy = async (text, categoryId) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié ✂️");
      trackEvent("copy_click", { source: "category", category_id: categoryId });
    } catch {
      toast.error("Copie impossible");
    }
  };

  return (
    <section id="categories" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
          Les grands classiques
        </h2>
        <span className="nb-border bg-[#FCD34D] px-3 py-1 text-xs font-black uppercase">
          Répliques toutes prêtes
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((cat) => {
          const open = openId === cat.id;
          const isFeatured = cat.featured;
          return (
            <article
              key={cat.id}
              data-testid={`category-card-${cat.id}`}
              className={
                "nb-card p-5 flex flex-col relative " +
                (isFeatured
                  ? "lg:col-span-3 md:col-span-2 nb-shadow-lg"
                  : "")
              }
              style={{ background: cat.color }}
            >
              {isFeatured && (
                <span
                  data-testid={`featured-badge-${cat.id}`}
                  className="absolute -top-3 -left-2 nb-border bg-[#FF4D85] text-white px-3 py-1 text-xs font-black uppercase rotate-[-3deg]"
                >
                  🔥 {cat.badge || "Sélection"}
                </span>
              )}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">{cat.emoji}</span>
                  <h3
                    className={
                      "font-display font-extrabold " +
                      (isFeatured ? "text-2xl sm:text-3xl" : "text-xl")
                    }
                  >
                    {cat.name}
                  </h3>
                </div>
              </div>

              <ul
                className={
                  "space-y-2 mb-4 " +
                  (isFeatured ? "md:grid md:grid-cols-2 md:gap-2 md:space-y-0" : "")
                }
              >
                {cat.examples.slice(0, isFeatured ? 5 : 2).map((ex, i) => (
                  <li
                    key={i}
                    className="nb-border bg-white/70 px-3 py-2 text-sm font-bold leading-tight"
                  >
                    {ex}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`expand-category-${cat.id}`}
                onClick={() => {
                  const willOpen = !open;
                  setOpenId(willOpen ? cat.id : null);
                  if (willOpen) {
                    trackEvent("category_expand", {
                      category_id: cat.id,
                      featured: !!cat.featured,
                    });
                  }
                }}
                className="mt-auto nb-border bg-white px-3 py-2 text-sm font-black flex items-center justify-between hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {open ? "Cacher les répliques" : "Voir les répliques prêtes"}
                <ChevronDown
                  size={16}
                  strokeWidth={3}
                  className={"transition-transform " + (open ? "rotate-180" : "")}
                />
              </button>

              {open && (
                <div className="mt-3 space-y-2" data-testid={`responses-${cat.id}`}>
                  {cat.ready_responses.map((r, i) => (
                    <div key={i} className="nb-border bg-white p-3">
                      <p className="font-bold text-sm leading-snug mb-2">« {r} »</p>
                      <div className="flex gap-2">
                        <button
                          data-testid={`cat-copy-${cat.id}-${i}`}
                          onClick={() => copy(r, cat.id)}
                          className="nb-border bg-[#FFFDF9] px-2 py-1 text-xs font-bold hover:bg-[#FCD34D]"
                        >
                          <Copy size={12} className="inline mr-1" /> Copier
                        </button>
                        <button
                          data-testid={`cat-fav-${cat.id}-${i}`}
                          onClick={() => {
                            onFavorite(r);
                            trackEvent("favorite_add", {
                              source: "category",
                              category_id: cat.id,
                            });
                          }}
                          className="nb-border bg-[#FFFDF9] px-2 py-1 text-xs font-bold hover:bg-[#FF4D85]"
                        >
                          <Heart size={12} className="inline mr-1" /> Favori
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
